import {
  Alert,
  Box,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import {
  useSmsConfig,
  useUpdateSmsConfig,
} from "../../modules/channels/sms/hooks/use-sms-config-hooks";

import type { FC } from "react";

interface OptOutFormData {
  optOutText: string;
}

export const SMS: FC = () => {
  const { data: smsConfig, loading, error, reload } = useSmsConfig();
  const updateSmsConfig = useUpdateSmsConfig();
  const [includeOptOutFooter, setIncludeOptOutFooter] = useState(false);
  const [prependCompanyName, setPrependCompanyName] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<OptOutFormData>({
    defaultValues: {
      optOutText: "Reply STOP to unsubscribe",
    },
    mode: "onBlur",
  });

  const currentOptOutText = watch("optOutText");

  useEffect(() => {
    if (smsConfig) {
      const hasFooter = smsConfig.footer !== null && smsConfig.footer !== "";
      setIncludeOptOutFooter(hasFooter);
      setPrependCompanyName(Boolean(smsConfig.prepend_company_name));
      if (hasFooter && smsConfig.footer) {
        reset({ optOutText: smsConfig.footer });
      }
    }
  }, [smsConfig, reset]);

  const handleFooterToggle = async (checked: boolean): Promise<void> => {
    setIncludeOptOutFooter(checked);
    try {
      if (checked) {
        await updateSmsConfig({ footer: currentOptOutText.trim() });
        reset({ optOutText: currentOptOutText.trim() });
      } else {
        await updateSmsConfig({ footer: null });
        reset({ optOutText: "" });
      }

      reload();
    } catch (error) {
      console.error("Failed to update SMS footer config:", error);
      toast.error("Failed to update SMS footer config");
    }
  };

  const handleCompanyNametoggle = async (checked: boolean): Promise<void> => {
    setPrependCompanyName(checked);
    try {
      await updateSmsConfig({ prepend_company_name: checked });
      reload();
    } catch (error) {
      console.error("Failed to update SMS company name config:", error);
      toast.error("Failed to update SMS company name config");
    }
  };

  const handleOptOutTextSubmit = async (data: OptOutFormData): Promise<void> => {
    const trimmedText = data.optOutText.trim();

    if (includeOptOutFooter && isDirty) {
      try {
        await updateSmsConfig({ footer: trimmedText });
        reset({ optOutText: trimmedText });
        reload();
      } catch (error) {
        console.error("Failed to update SMS config:", error);
      }
    }
  };

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">Failed to load SMS configuration</Alert>;

  return (
    <>
      <Box>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          SMS compliance is an important factor in order to send messages to your employees
          reliably. Each individual customer is required to have their own SMS telephone number,
          which they are accountable for. Each SMS telephone number will have a reputation score
          that SMS authorities give depending on the content you send. The higher this score is, the
          better for your deliverability. ChangeEngine is able to act as a support and agent to help
          make this simple. Learn more here.
        </Typography>
        <Typography sx={{ fontSize: 14, mb: 4 }}>
          To improve your reputation score, you can enable the following settings.
        </Typography>
        <Typography variant="h6" sx={{ mb: 4 }}>
          SMS Compliance Settings
        </Typography>

        <Stack>
          <FormControlLabel
            control={
              <Switch
                checked={prependCompanyName}
                onChange={(e) => void handleCompanyNametoggle(e.target.checked)}
              />
            }
            label="Include company name"
          />

          <FormControlLabel
            control={
              <Switch
                checked={includeOptOutFooter}
                onChange={(e) => void handleFooterToggle(e.target.checked)}
              />
            }
            label="Include opt-out footer"
          />
        </Stack>

        {includeOptOutFooter && (
          <Stack spacing={2} sx={{ mt: 2 }}>
            <Controller
              name="optOutText"
              control={control}
              rules={{
                required: "This message is required",
                maxLength: {
                  value: 100,
                  message: "Message cannot exceed 100 characters",
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Opt-out message"
                  variant="outlined"
                  size="small"
                  sx={{ width: "400px" }}
                  error={Boolean(errors.optOutText)}
                  helperText={errors.optOutText?.message || ""}
                  onBlur={() => {
                    field.onBlur();
                    void handleSubmit(handleOptOutTextSubmit)();
                  }}
                />
              )}
            />
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              This text will be added to the bottom of each SMS message. We recommend:
              <br /> 1. Reply STOP to unsubscribe
              <br /> 2. Shouldn’t be receiving this? Contact: [email/phone]
            </Typography>
          </Stack>
        )}
      </Box>
    </>
  );
};
